import React, { useState } from 'react';

interface ContactPageProps {
  onNavigate?: (path: string, view: string, tab?: string) => void;
}

export const ContactPage: React.FC<ContactPageProps> = ({ onNavigate }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    category: 'formula-verification',
    subject: '',
    message: '',
  });

  const [submitted, setSubmitted] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const handleNav = (path: string, view: string, tab: string = 'water') => {
    if (onNavigate) {
      onNavigate(path, view, tab);
    } else {
      window.location.href = path;
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errorMsg) setErrorMsg(null);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      setErrorMsg('Please enter your name.');
      return;
    }
    if (!formData.email.trim() || !formData.email.includes('@')) {
      setErrorMsg('Please enter a valid email address so we can reply to your inquiry.');
      return;
    }
    if (!formData.subject.trim()) {
      setErrorMsg('Please provide a subject line for your message.');
      return;
    }
    if (!formData.message.trim() || formData.message.trim().length < 15) {
      setErrorMsg('Please provide a descriptive message (at least 15 characters).');
      return;
    }

    // In client-side environment, register submission state
    setErrorMsg(null);
    setSubmitted(true);
  };

  const handleReset = () => {
    setFormData({
      name: '',
      email: '',
      category: 'formula-verification',
      subject: '',
      message: '',
    });
    setSubmitted(false);
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <article className="bg-white dark:bg-[#1F2937] rounded-3xl p-6 sm:p-10 lg:p-12 space-y-8 shadow-xs border border-slate-200 dark:border-slate-700">
        <header className="border-b border-slate-100 dark:border-slate-700/80 pb-6">
          <span className="text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full bg-teal-50 dark:bg-teal-900/40 text-[#087F9C] dark:text-[#39BFD3] border border-teal-200/60 dark:border-teal-800/60 inline-block mb-3">
            Contact &amp; Feedback
          </span>
          <h1 className="font-display font-extrabold text-3xl sm:text-4xl text-[#10283D] dark:text-white tracking-tight">
            Contact Us &amp; Technical Feedback
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-2">
            Have a question about our hydraulic calculations, found a potential formula discrepancy, or want to suggest a new pipe standard? Let our engineering team know.
          </p>
        </header>

        {/* What Users Can Contact Us About */}
        <section className="space-y-4 text-sm sm:text-base text-slate-700 dark:text-slate-300 leading-relaxed">
          <h2 className="font-display font-bold text-xl text-[#10283D] dark:text-white">
            What You Can Contact Us About
          </h2>
          <p>
            We welcome constructive feedback, peer review, and suggestions from practicing engineers, consultants, and contractors. You can reach out regarding:
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-1">
            <div className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700">
              <h3 className="font-display font-bold text-sm text-[#10283D] dark:text-white mb-1">
                📐 Engineering Formula Verification
              </h3>
              <p className="text-xs text-slate-600 dark:text-slate-300">
                Inquiries or peer-review observations regarding our Darcy-Weisbach friction solver, Colebrook-White Swamee-Jain models, or CIBSE velocity limits.
              </p>
            </div>

            <div className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700">
              <h3 className="font-display font-bold text-sm text-[#10283D] dark:text-white mb-1">
                🐛 Technical Bugs &amp; Errors
              </h3>
              <p className="text-xs text-slate-600 dark:text-slate-300">
                Reporting interface glitches, unit conversion anomalies, responsive layout problems on specific mobile devices, or PDF report formatting bugs.
              </p>
            </div>

            <div className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700">
              <h3 className="font-display font-bold text-sm text-[#10283D] dark:text-white mb-1">
                📚 Standards &amp; Materials Requests
              </h3>
              <p className="text-xs text-slate-600 dark:text-slate-300">
                Proposing additions to our pipe database (such as specialized stainless steel grades, regional plastic standards, or alternative pipe schedules).
              </p>
            </div>

            <div className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700">
              <h3 className="font-display font-bold text-sm text-[#10283D] dark:text-white mb-1">
                💡 General Suggestions &amp; UI Feedback
              </h3>
              <p className="text-xs text-slate-600 dark:text-slate-300">
                Ideas for new calculation modules, usability improvements, or technical reference guide topics you would like covered.
              </p>
            </div>
          </div>
        </section>

        {/* Contact Form Section */}
        <section className="pt-2">
          <div className="bg-slate-50 dark:bg-slate-800/70 p-6 sm:p-8 rounded-3xl border border-slate-200 dark:border-slate-700">
            <h2 className="font-display font-bold text-xl text-[#10283D] dark:text-white mb-2">
              Send a Message to Our Engineering Review Team
            </h2>
            <p className="text-xs text-slate-600 dark:text-slate-300 mb-6 leading-relaxed">
              Fill out the form below. If you are reporting a calculation discrepancy, please include the specific pipe material, diameter, temperature, and design flow rate used.
            </p>

            {submitted ? (
              <div className="p-6 bg-teal-50 dark:bg-teal-950/40 border border-teal-200 dark:border-teal-800/70 rounded-2xl text-center space-y-3">
                <div className="w-12 h-12 mx-auto rounded-full bg-[#087F9C] text-white flex items-center justify-center text-xl font-bold">
                  ✓
                </div>
                <h3 className="font-display font-bold text-lg text-[#10283D] dark:text-white">
                  Thank You for Your Feedback!
                </h3>
                <p className="text-xs text-slate-600 dark:text-slate-300 max-w-md mx-auto leading-relaxed">
                  Your message has been recorded. Our engineering editorial team reviews submissions periodically to improve calculation accuracy and user experience.
                </p>
                <button
                  type="button"
                  onClick={handleReset}
                  className="mt-2 px-4 py-2 bg-[#087F9C] text-white text-xs font-semibold rounded-xl hover:bg-teal-700 transition-colors"
                >
                  Send Another Message
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                {errorMsg && (
                  <div className="p-3 bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-800 text-rose-700 dark:text-rose-300 text-xs rounded-xl font-medium">
                    ⚠️ {errorMsg}
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label
                      htmlFor="contact-name"
                      className="block text-xs font-bold text-slate-700 dark:text-slate-200 mb-1"
                    >
                      Your Name *
                    </label>
                    <input
                      type="text"
                      id="contact-name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="e.g. John Doe, P.E."
                      className="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 text-xs focus:outline-none focus:ring-2 focus:ring-[#087F9C]"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="contact-email"
                      className="block text-xs font-bold text-slate-700 dark:text-slate-200 mb-1"
                    >
                      Your Email Address *
                    </label>
                    <input
                      type="email"
                      id="contact-email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="name@example.com"
                      className="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 text-xs focus:outline-none focus:ring-2 focus:ring-[#087F9C]"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label
                      htmlFor="contact-category"
                      className="block text-xs font-bold text-slate-700 dark:text-slate-200 mb-1"
                    >
                      Inquiry Category *
                    </label>
                    <select
                      id="contact-category"
                      name="category"
                      value={formData.category}
                      onChange={handleChange}
                      className="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 text-xs focus:outline-none focus:ring-2 focus:ring-[#087F9C]"
                    >
                      <option value="formula-verification">Formula / Calculation Verification</option>
                      <option value="bug-report">Bug or Technical Glitch</option>
                      <option value="standard-request">New Standard or Material Request</option>
                      <option value="ui-suggestion">UI / Feature Suggestion</option>
                      <option value="general">General Inquiries</option>
                    </select>
                  </div>

                  <div>
                    <label
                      htmlFor="contact-subject"
                      className="block text-xs font-bold text-slate-700 dark:text-slate-200 mb-1"
                    >
                      Subject Line *
                    </label>
                    <input
                      type="text"
                      id="contact-subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      placeholder="Summary of inquiry or issue"
                      className="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 text-xs focus:outline-none focus:ring-2 focus:ring-[#087F9C]"
                    />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="contact-message"
                    className="block text-xs font-bold text-slate-700 dark:text-slate-200 mb-1"
                  >
                    Your Message / Technical Details *
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    rows={5}
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Please include full details (e.g., calculator used, input values, expected results, standard referenced, or browser details)..."
                    className="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 text-xs focus:outline-none focus:ring-2 focus:ring-[#087F9C]"
                  />
                </div>

                <div className="pt-2">
                  <button
                    type="submit"
                    className="w-full sm:w-auto px-6 py-3 bg-[#087F9C] hover:bg-teal-700 text-white font-bold text-xs rounded-xl shadow-sm transition-colors flex items-center justify-center gap-2"
                  >
                    <span>Submit Feedback</span>
                    <span>→</span>
                  </button>
                </div>
              </form>
            )}
          </div>
        </section>

        {/* Technical Notice */}
        <section className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700 text-xs text-slate-500 dark:text-slate-400 space-y-1">
          <p>
            <strong>Engineering Privacy Notice:</strong> Feedback and technical queries submitted through this form are used exclusively to address technical reports and improve our tools. We do not sell or share contact details with marketing third parties.
          </p>
        </section>

        {/* Navigation Quick Links */}
        <footer className="pt-6 border-t border-slate-100 dark:border-slate-700/80 flex flex-wrap gap-3 text-xs font-semibold">
          <button
            onClick={() => handleNav('/', 'calculators', 'water')}
            className="px-4 py-2.5 bg-[#087F9C] text-white rounded-xl hover:bg-teal-700 transition-colors"
          >
            Launch Water Pipe Sizing &rarr;
          </button>
          <button
            onClick={() => handleNav('/about-us/', 'about')}
            className="px-4 py-2.5 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 rounded-xl hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
          >
            About Us &rarr;
          </button>
          <button
            onClick={() => handleNav('/disclaimer/', 'disclaimer')}
            className="px-4 py-2.5 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 rounded-xl hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
          >
            Engineering Disclaimer &rarr;
          </button>
        </footer>
      </article>
    </div>
  );
};
